import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FormProvider, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { Button, Label, Spinner } from "@inkblot-therapy/squid"
import { useFlags } from "launchdarkly-react-client-sdk"
import { usePostMapCsv, usePostValidateCsv } from "../../hooks/useUploadCsv"
import { FileUpload } from "../FileUpload"
import { Alert } from "../Alert"
import {
  extractNumberInParentheses,
  splitProvinceString,
} from "../../utils/utilityFunctions/utilityFunction"
import { FormErrorText } from "../FormErrorText"
import { useGetFormByProvince } from "../../hooks/useGetFormByProvince"
import { useToastDispatch } from "../../context/ToastContextProvider"
import { Toast } from "../../types/ToastState"
import { FeatureFlagConfig } from "../../types/FeatureFlags"
import { ValidationResponse } from "../../types/ValidationResponse"
import { UploadResult } from "./UploadResult"
import classNames from "classnames"
import { UploadActions } from "./UploadActions"
import { DataBaseService } from "../../services/DatabaseService"
import { UploadedState } from "./UploadedState"
import { UploadEditInfo } from "./UploadEditInfo"
import ReCaptcha from "react-google-recaptcha"
import { EnvironmentService } from "../../services/EnvironmentService"
import { ReferralPayload } from "../../types/ReferralPayload"

export function UploadLandingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const methods = useForm({
    defaultValues: { file: null, province: "" },
    shouldUnregister: false,
  })
  const { province } = useParams<{ province: string }>()
  const { data } = useGetFormByProvince(province ?? "ontario")
  const { validateCsv, isLoading } = usePostValidateCsv()
  const { mapCsv, isLoading: isMapping } = usePostMapCsv()
  const { useReferralVersion2 } = useFlags<FeatureFlagConfig>()

  const [file, setFile] = useState<File | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [validationHasErrors, setValidationHasErrors] = useState<boolean>(false)
  const [isReviewing, setIsReviewing] = useState<boolean>(false)
  const [validationResponse, setValidationResponse] = useState<
    ValidationResponse | undefined
  >()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  var recaptchaSiteKey = EnvironmentService.getReCaptchaSiteKey()
  const isRecaptchaRequired = EnvironmentService.getEnv() === "production"

  const dispatch = useToastDispatch()
  const [token, setToken] = useState("")
  const isValidationSuccess = !error && !validationHasErrors && file
  const showUploadResult =
    (useReferralVersion2 && !isValidationSuccess) || isReviewing

  const uploadCsvWrapper = (file: File, province: string) => {
    return new Promise((resolve, reject) => {
      const values = { file, province }
      validateCsv(values, {
        onSuccess: (data) => {
          resolve(data)
          // navigate(`../${step}?status=success&id=${data.client_id}`)
        },
        onError: (err) => {
          const errorMessage =
            err.response?.data?.error || err.response?.statusText
          setError(errorMessage)
          setFile(undefined)
          setValidationResponse(undefined)
          reject(err)
        },
      })
    })
  }

  const mapCsvWrapper = (file: File, province: string) => {
    return new Promise((resolve, reject) => {
      const values = { file, province }
      mapCsv(values, {
        onSuccess: (resp) => {
          sessionStorage.setItem(
            `form-state-${province}`,
            JSON.stringify(resp, null, 2),
          )
          resolve(data)
          navigate(`../steps/${(data?.length || 0) + 1}`)
          dispatch({
            type: "ADD_TOAST",
            toast: {
              status: "success",
              header: t("client.uploadCsv.successToastHeader"),
              text: t("client.uploadCsv.successToast"),
            } as Toast,
          })
        },
        onError: () => {
          setError(undefined)
          setFile(undefined)
          reject(null)
        },
      })
    })
  }

  const onSubmit = () => {
    methods.handleSubmit(async () => {
      if (useReferralVersion2) {
        const referralData: Record<string, string | number>[] =
          validationResponse?.data.map((resp) => {
            const key = Object.keys(resp)[0]
            if (key === "osp_organization_id") {
              return { [key]: extractNumberInParentheses(resp[key].value) }
            }
            return { [key]: resp[key].value }
          }) || []
        try {
          setIsSubmitting(true)
          const payload: ReferralPayload = {
            referralData,
            "g-recaptcha-response": token,
          }
          await DataBaseService.submitOntarioReferralV2(payload)
          navigate(`/${province}/success`)
          setIsSubmitting(false)
        } catch (err) {
          setIsSubmitting(false)
          navigate(`/${province}/error`)
        }
        return
      }
      const provinceParam =
        splitProvinceString(province?.toLowerCase()) || "ontario"
      try {
        if (!province || !file) {
          throw new Error(t("client.submission.error.missingProvince"))
        }
        await mapCsvWrapper(file, provinceParam)
      } catch (err) {
        // navigate(`../${step}?status=error`)
      }
    })()
  }
  const onUpload = async (value: File) => {
    setError(undefined)
    setFile(value)
    const provinceParam =
      splitProvinceString(province?.toLowerCase()) || "ontario"
    try {
      if (!province || !value) {
        throw new Error(t("client.submission.error.missingProvince"))
      }
      const validateResponse = await uploadCsvWrapper(value, provinceParam)
      // if reverral v2 is enabled, it means we won't use the form, we'll display the validation results instead
      if (useReferralVersion2) {
        const response = validateResponse as ValidationResponse
        setValidationResponse(response)
        // check if there are any errors
        for (const data of response.data) {
          if (data[Object.keys(data)[0]].errors.length) {
            setValidationHasErrors(true)
            return
          }
        }
        setValidationHasErrors(false)
      }
    } catch (err) {
      // navigate(`../${step}?status=error`)
    }
  }

  const onRemoveFile = () => {
    setFile(undefined)
    setError(undefined)
    setValidationResponse(undefined)
  }

  const i18nKey = useReferralVersion2 ? "landingPageV2" : "landingPage"

  if (isSubmitting && useReferralVersion2) {
    return (
      <div className="flex flex-col items-center justify-center mt-20">
        <Label>{t("client.submission.loading")}</Label>
        <Spinner size="lg" className="mb-4" />
      </div>
    )
  }

  return (
    <div className="my-6 mr-20 mt-28">
      <div className="flex flex-col h-[100vh] items-center max-w-3xl mx-auto">
        <div className="flex flex-col">
          <div className="flex flex-col">
            <div className="flex justify-between pb-[56px] text-[color:var(--core-color-slate-90)] [font-family:var(--alias-font-display-xl-font-family)] [font-size:var(--alias-font-display-xl-font-size)]">
              <p>{t(`client.uploadCsv.${i18nKey}.title`)}</p>
              {!useReferralVersion2 && (
                <Button
                  onClick={() => navigate("../steps/1")}
                  variant="outlined"
                  leftIcon="ArrowLeft"
                >
                  {t("client.buttons.cancelReturnForm")}
                </Button>
              )}
            </div>
            <p className="whitespace-pre-line text-[color:var(--alias-color-text-weaker)] [font-size:var(--alias-font-subtitle-md-font-size)] [font-family:var(--alias-font-subtitle-lg-font-weight)]">
              {t(`client.uploadCsv.${i18nKey}.description`)}
            </p>
          </div>
          <div
            className={classNames({
              "pb-[56px]": !useReferralVersion2,
              "mb-4": useReferralVersion2,
            })}
          />
          {!useReferralVersion2 && (
            <div className="flex mb-4 w-full">
              <Alert status="neutral" size="lg" icon="InfoWrapped">
                {t("client.uploadCsv.landingPage.warning")}
              </Alert>
            </div>
          )}
          <FormProvider {...methods}>
            <div>
              <FileUpload
                onUpload={onUpload}
                onRemove={onRemoveFile}
                title={t("client.uploadCsv.landingPage.uploadDrag")}
                accept=".csv"
                isLoading={isLoading}
                file={file}
                setFile={setFile}
              />
              {error && (
                <FormErrorText className="pt-[var(--alias-padding-xxs)]">
                  {error}
                </FormErrorText>
              )}
              {useReferralVersion2 && validationResponse && (
                <UploadedState
                  error={error}
                  validationHasErrors={validationHasErrors}
                  file={file}
                  onRemoveFile={onRemoveFile}
                  isReviewing={isReviewing}
                  setIsReviewing={setIsReviewing}
                  setFile={setFile}
                />
              )}
              {validationResponse && showUploadResult && (
                <UploadResult validationResponse={validationResponse} />
              )}
              {isValidationSuccess && useReferralVersion2 && <UploadEditInfo />}
            </div>
            {useReferralVersion2 && (
              <>
                <div className="flex justify-end mb-4 pt-4">
                  {isRecaptchaRequired && (
                    <ReCaptcha
                      onChange={(token) => token && setToken(token)}
                      hidden={false}
                      sitekey={recaptchaSiteKey ?? ""}
                    />
                  )}
                </div>
                <UploadActions
                  submitHandler={onSubmit}
                  submitDisabled={
                    Boolean(!validationResponse) ||
                    Boolean(isRecaptchaRequired && !token) ||
                    Boolean(error) ||
                    validationHasErrors
                  }
                />
              </>
            )}
            {!useReferralVersion2 && (
              <div className="flex flex-col items-end mt-5">
                <Button
                  onClick={onSubmit}
                  variant="primary"
                  disabled={
                    !file ||
                    !!error ||
                    (isMapping && Boolean(isValidationSuccess))
                  }
                >
                  {isMapping
                    ? t("client.buttons.fillingForm")
                    : t("client.buttons.fillFormContinue")}
                </Button>
              </div>
            )}
          </FormProvider>
        </div>
      </div>
    </div>
  )
}
